import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Task from './Task';
import TaskForm from './TaskForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCheckSquare, faSquare, faUnlink } from '@fortawesome/free-solid-svg-icons';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useNavigate } from 'react-router-dom';

const Goal = ({ goal, taskCategories, fetchGoals, deleteGoal, rewards, fetchRewards }) => {
    const [selectedReward, setSelectedReward] = useState([]);
    const navigate = useNavigate();

    // Calculate total invested time
    const calculateInvestedTime = (tasks) => {
        return tasks.reduce((total, task) => total + (task.investedTime || 0), 0);
    };

    // Calculate days remaining until deadline
    const calculateDaysRemaining = (deadline) => {
        if (!deadline) return 'No deadline set';
        const deadlineDate = new Date(deadline);
        const today = new Date();
        const timeDiff = deadlineDate - today;
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
        return daysDiff > 0 ? `目标剩余天数: ${daysDiff}` : `Deadline passed`;
    };

    const handleEditGoal = async (goalId, field, value) => {
        console.log(`handleEditGoal called with goalId: ${goalId}, field: ${field}, value: ${value}`);
        const updatedGoal = { ...goal, [field]: value };
        if (field === 'createdDate' || field === 'deadline') {
            updatedGoal[field] = new Date(value).toISOString();
            console.log(`Updated ${field} to: ${updatedGoal[field]}`);
        }
        try {
            await axios.put(`/goals/${goalId}`, updatedGoal);
            console.log(`Successfully updated goal with ID: ${goalId}`);
            fetchGoals();
        } catch (error) {
            console.error('Error updating goal:', error);
        }
    };

    // Toggle goal completion status
    const toggleGoalComplete = async (goalId) => {
        const updatedGoal = { ...goal, isComplete: !goal.isComplete };
        try {
            await axios.put(`/goals/${goalId}`, updatedGoal);
            if (updatedGoal.isComplete && updatedGoal.rewardId) {
                await axios.put(`/rewards/${updatedGoal.rewardId._id}/status`, { status: 'available' });
            }
            fetchGoals();
        } catch (error) {
            console.error('Error toggling goal complete:', error);
        }
    };

    // Delete a task from the goal
    const deleteTask = async (goalId, taskId) => {
        try {
            await axios.delete(`/goals/${goalId}/tasks/${taskId}`);
            fetchGoals();
        } catch (error) {
            console.error('Error deleting task:', error);
        }
    };

    // Handle reward selection
    const handleRewardSelect = async (selected) => {
        if (selected.length > 0) {
            const rewardId = selected[0];
            try {
                await axios.put(`/goals/${goal._id}/reward`, { rewardId });
                fetchGoals();
                fetchRewards(); // Update unbound rewards list
                setSelectedReward([]); // Reset selected reward to avoid stale selection
            } catch (error) {
                console.error('Error binding reward:', error);
            }
        }
    };

    // Unbind reward from the goal
    const handleUnbindReward = async () => {
        try {
            if (goal.rewardId) {
                await axios.put(`/goals/${goal._id}/reward`, { rewardId: null });
                fetchGoals();
                fetchRewards(); // Update unbound rewards list
            }
        } catch (error) {
            console.error('Error unbinding reward:', error);
        }
    };
    //奖励点击跳转
    const handleRewardClick = (rewardId) => {
        navigate(`/rewards?rewardId=${rewardId}`);
    };

    useEffect(() => {
        if (goal.tasks.length === 0) return; // If no tasks, return immediately
        const allTasksComplete = goal.tasks.every(task => task.isComplete);
        if (allTasksComplete && !goal.isComplete) {
            toggleGoalComplete(goal._id);
        }
    }, [goal.tasks, goal._id, goal.isComplete]);

    // Set background image style and text shadow
    const backgroundImageStyle = goal.rewardId ? { backgroundImage: `url(/uploads/${goal.rewardId.file})`, backgroundSize: 'cover', position: 'relative', color: 'white', textShadow: '2px 2px 4px black' } : {};

    // Calculate the total invested time
    const totalInvestedTime = calculateInvestedTime(goal.tasks);

    const formatDate = (date) => {
        return date ? new Date(date).toISOString().split('T')[0] : '';
    };

    return (
        <div className={`card mb-4 ${goal.isComplete ? 'bg-success text-white' : ''}`} style={backgroundImageStyle}>
            <div className="card-header d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 1 }}>
                <div>
                    <h5 contentEditable suppressContentEditableWarning
                        onBlur={(e) => handleEditGoal(goal._id, 'name', e.target.innerText)}
                        style={{color: 'white', textShadow: '2px 2px 4px black'}}>
                        {goal.name}
                    </h5>
                    <p contentEditable suppressContentEditableWarning
                       onBlur={(e) => handleEditGoal(goal._id, 'description', e.target.innerText)}
                       style={{color: 'white', textShadow: '2px 2px 4px black'}}>
                        {goal.description}
                    </p>
                    <div className="mb-3">
                        <Typeahead
                            id="goal-category"
                            options={taskCategories}
                            selected={goal.category ? [goal.category] : []}
                            onChange={(selected) => handleEditGoal(goal._id, 'category', selected[0] || '')}
                            placeholder="Select or add category"
                            allowNew
                            newSelectionPrefix="Add a new category: "
                        />
                    </div>
                    <span style={{color: 'white', textShadow: '2px 2px 4px black'}}>
                        预计投入时长: <span contentEditable suppressContentEditableWarning
                                            onBlur={(e) => handleEditGoal(goal._id, 'expectedTime', e.target.innerText)}>
                            {goal.expectedTime}
                        </span> mins
                    </span>
                    <br/>
                    <span style={{color: 'white', textShadow: '2px 2px 4px black'}}>
                        累计投入时长: {totalInvestedTime} mins
                    </span>
                    <br/>
                    <span style={{color: 'white', textShadow: '2px 2px 4px black'}}>
                        目标创建日期: <input type="date" value={formatDate(goal.createdDate)}
                                             onChange={(e) => handleEditGoal(goal._id, 'createdDate', e.target.value)}/>
                    </span>
                    <br/>
                    <span style={{color: 'white', textShadow: '2px 2px 4px black'}}>
                        目标截止日期: <input type="date" value={formatDate(goal.deadline)}
                                             onChange={(e) => handleEditGoal(goal._id, 'deadline', e.target.value)}/>
                        ({calculateDaysRemaining(goal.deadline)})
                    </span>
                    {goal.rewardId ? (
                        <div>
                            所绑定奖励: <a href={`/rewards?rewardId=${goal.rewardId._id}`} onClick={(e) => {
                            e.preventDefault();
                            handleRewardClick(goal.rewardId._id);
                        }} style={{color: 'white', textShadow: '2px 2px 4px black'}}>
                            {goal.rewardId.name}
                        </a>
                            <button className="btn btn-warning ms-2" onClick={handleUnbindReward}>
                                <FontAwesomeIcon icon={faUnlink}/>
                            </button>
                        </div>
                    ) : (
                        <Typeahead
                            id="reward-selector"
                            labelKey="name"
                            options={rewards}
                            onChange={handleRewardSelect}
                            placeholder="Choose a reward to bind..."
                            selected={selectedReward}
                        />
                    )}
                </div>
                <div>
                    <button className="btn btn-sm" onClick={() => toggleGoalComplete(goal._id)}>
                        <FontAwesomeIcon icon={goal.isComplete ? faCheckSquare : faSquare}/>
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteGoal(goal._id)}>
                        <FontAwesomeIcon icon={faTrash}/>
                    </button>
                </div>
            </div>
            <div className="card-body" style={{position: 'relative', zIndex: 1}}>
                <h5 style={{color: 'white', textShadow: '2px 2px 4px black' }}>Tasks</h5>
                {goal.tasks.map((task) => (
                    <Task key={task._id} task={task} goalId={goal._id} taskCategories={taskCategories} fetchGoals={fetchGoals} deleteTask={deleteTask} />
                ))}
                <TaskForm goalId={goal._id} taskCategories={taskCategories} goalCategory={goal.category} fetchGoals={fetchGoals} />
            </div>
        </div>
    );
};

export default Goal;
